import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import {
  Card, Button, Badge, SearchInput, Select, Spinner, EmptyState,
  Table, TableHead, TableBody, TableRow, TableCell, Modal,
  stickyActionCellClass, stickyActionHeaderClass
} from '../../components/UI';
import api, { recordingsAPI, callAnalysisAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlinePlay, HiOutlineDownload, HiOutlineTrash, HiOutlineMicrophone,
  HiOutlineChevronUp, HiOutlineChevronDown, HiOutlineSparkles, HiOutlineClipboardList
} from 'react-icons/hi';
import { format } from 'date-fns';
import { LANGUAGE_OPTIONS, buildProviderOptions } from '../CallAnalysis/languages';
import TalkTimeAudio from './TalkTimeAudio';

// Download name matching the delivered file: formats browsers can't play
// (AMR, AWB, 3GP, …) are served converted to MP3, so name those ".mp3".
const recordingDownloadName = (fileName, blobType, id) => {
  const base = (fileName || `recording-${id}`).replace(/\.[^.]+$/, '');
  const originalExt = (fileName || '').match(/\.[^.]+$/)?.[0] || '.mp3';
  return base + (blobType === 'audio/mpeg' ? '.mp3' : originalExt);
};

// Build compact pagination with ellipsis
const buildPageList = (current, total) => {
  const pages = [];
  const set = new Set([1, total, current, current - 1, current + 1]);
  for (const p of set) {
    if (p >= 1 && p <= total) pages.push(p);
  }
  pages.sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < pages.length; i++) {
    result.push(pages[i]);
    if (i < pages.length - 1 && pages[i + 1] - pages[i] > 1) {
      result.push('...');
    }
  }
  return result;
};

const RecordingsList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [userFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordingToDelete, setRecordingToDelete] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);

  // Bulk "send for analysis" state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysisLanguage, setAnalysisLanguage] = useState('multi');
  const [analysisProvider, setAnalysisProvider] = useState('');
  const [sendingAnalysis, setSendingAnalysis] = useState(false);

  // Provider availability (Deepgram key / LLM options) for the analysis modal
  const { data: providersData } = useQuery({
    queryKey: ['call-analysis-providers'],
    queryFn: async () => {
      const response = await callAnalysisAPI.getProviders();
      return response.data?.data || {};
    },
    staleTime: 0, // always re-check so buttons reflect the server immediately
    refetchOnMount: 'always',
  });
  const deepgramEnabled = providersData?.deepgramEnabled ?? true;
  const defaultProvider = providersData?.llm?.default || 'openai';

  // Fetch audio URL with authentication when recording is selected
  useEffect(() => {
    if (selectedRecording) {
      const fetchAudio = async () => {
        try {
          const response = await api.get(`/recordings/file/${selectedRecording._id}`, {
            responseType: 'blob'
          });
          const url = URL.createObjectURL(response.data);
          setAudioUrl(url);
        } catch (error) {
          console.error('Failed to load audio:', error);
          toast.error('Failed to load recording');
        }
      };
      fetchAudio();
    } else {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }
    }
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRecording?._id]);

  // Fetch recordings with filters
  const { data: recordingsData, isLoading, refetch } = useQuery({
    queryKey: ['recordings', page, pageSize, userFilter, statusFilter, search, sortBy, sortOrder],
    queryFn: async () => {
      const params = { page, limit: pageSize };
      if (userFilter) params.userId = userFilter;
      if (statusFilter) params.uploadStatus = statusFilter;
      if (search) params.search = search;
      if (sortBy) params.sortBy = sortBy;
      if (sortOrder) params.sortOrder = sortOrder;
      const response = await recordingsAPI.getAll(params);
      return response.data || {};
    },
  });

  // Fetch statistics
  const { data: stats } = useQuery({
    queryKey: ['recordingStats'],
    queryFn: async () => {
      const response = await recordingsAPI.getAll({ limit: 1 });
      return response.data?.stats || {};
    },
  });

  const recordings = recordingsData?.data || [];
  const pagination = recordingsData?.pagination || {};
  // Uploaded / Pending / Failed are counted from the current page only
  const statusCountsArePageScoped = (pagination.total || 0) > recordings.length;


  // Sort handler — cycles: asc → desc → clear
  const handleSort = (field) => {
    if (sortBy === field) {
      if (sortOrder === 'asc') {
        setSortOrder('desc');
      } else {
        setSortBy('');
        setSortOrder('');
      }
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) {
      return (
        <span className="inline-flex flex-col ml-0.5 opacity-30">
          <HiOutlineChevronUp className="w-3 h-3 -mb-1" />
          <HiOutlineChevronDown className="w-3 h-3" />
        </span>
      );
    }
    return sortOrder === 'asc' ? (
      <HiOutlineChevronUp className="w-3.5 h-3.5 ml-0.5 text-primary-600" />
    ) : (
      <HiOutlineChevronDown className="w-3.5 h-3.5 ml-0.5 text-primary-600" />
    );
  };
  const totalSize = stats?.totalSize || 0;

  // ─── Bulk selection ────────────────────────────────────────────────────────
  const allOnPageSelected =
    recordings.length > 0 && recordings.every((rec) => selectedIds.has(rec._id));

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(allOnPageSelected ? new Set() : new Set(recordings.map((rec) => rec._id)));
  };

  // Per-recording "Call Analysis" button: opens the existing analysis, or
  // starts one for recordings not analyzed yet, then opens the analysis page
  // (it live-tracks transcription/analysis progress).
  const handleCallAnalysis = async (rec) => {
    if (rec.analysis?._id) {
      navigate(`/app/call-analysis/${rec.analysis._id}`);
      return;
    }
    try {
      const response = await callAnalysisAPI.analyzeExisting({ recordingId: rec._id });
      toast.success('Call analysis started');
      const analysisId = response.data?.data?.analysisId || response.data?.analysisId;
      if (analysisId) navigate(`/app/call-analysis/${analysisId}`);
    } catch (error) {
      // 409 = an analysis already exists for this recording → open it
      const existingId = error.response?.status === 409
        ? error.response?.data?.data?.analysisId
        : null;
      if (existingId) {
        navigate(`/app/call-analysis/${existingId}`);
      } else {
        toast.error(error.response?.data?.message || 'Failed to start call analysis');
      }
    }
  };

  const handleSendForAnalysis = async () => {
    setSendingAnalysis(true);
    try {
      const payload = { recordingIds: [...selectedIds], language: analysisLanguage };
      if (analysisProvider) payload.llmProvider = analysisProvider;
      const response = await callAnalysisAPI.analyzeMany(payload);
      const { queued, skipped } = response.data || {};
      toast.success(`Queued ${queued} recording(s) for analysis${skipped ? ` (${skipped} already had an analysis)` : ''}`);
      setShowAnalysisModal(false);
      setSelectedIds(new Set());
      // Track progress on the Call Analysis page
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to queue analyses');
    } finally {
      setSendingAnalysis(false);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle delete recording
  const handleDeleteRecording = async () => {
    if (!recordingToDelete) return;

    try {
      await recordingsAPI.delete(recordingToDelete._id);
      toast.success('Recording deleted successfully');
      setShowDeleteModal(false);
      setRecordingToDelete(null);
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete recording');
    }
  };

  // Export recordings list
  const handleExport = () => {
    if (recordings.length === 0) {
      toast.error('No recordings to export');
      return;
    }

    const csvRows = [];
    csvRows.push('Phone Number,Lead Name,User,Call Type,Status,Duration,File Size,Created At');

    recordings.forEach((rec) => {
      csvRows.push([
        `"${rec.phoneNumber || ''}"`,
        `"${rec.leadId?.name || 'Unknown'}"`,
        `"${rec.userId?.firstName || ''} ${rec.userId?.lastName || ''}"`,
        `"${rec.callType || 'outgoing'}"`,
        `"${rec.callStatus || 'connected'}"`,
        `"${formatDuration(rec.duration)}"`,
        `"${formatFileSize(rec.fileSize)}"`,
        `"${rec.createdAt ? format(new Date(rec.createdAt), 'yyyy-MM-dd HH:mm') : ''}"`
      ].join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `recordings-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    toast.success('Export successful');
  };

  // Get upload status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'uploaded':
        return <Badge variant="success" size="sm">Uploaded</Badge>;
      case 'pending':
        return <Badge variant="warning" size="sm">Pending</Badge>;
      case 'failed':
        return <Badge variant="danger" size="sm">Failed</Badge>;
      default:
        return <Badge variant="default" size="sm">{status}</Badge>;
    }
  };

  // Get call type badge
  const getCallTypeBadge = (callType) => {
    switch (callType) {
      case 'incoming':
        return <Badge variant="info" size="sm">Incoming</Badge>;
      case 'missed':
        return <Badge variant="danger" size="sm">Missed</Badge>;
      default:
        return <Badge variant="primary" size="sm">Outgoing</Badge>;
    }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Recordings</h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5 sm:mt-1">
              Manage call recordings (auto-deleted after 30 days)
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            icon={<HiOutlineDownload className="w-4 h-4" />}
            onClick={handleExport}
          >
            <span className="hidden sm:inline">Export</span>
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 lg:gap-4">
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center">
            <p className="text-xs text-gray-500">Total Recordings</p>
            <p className="text-base sm:text-lg lg:text-2xl font-bold text-gray-900">
              {pagination.total || 0}
            </p>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-blue-50">
            <p className="text-xs text-blue-600">Total Size</p>
            <p className="text-base sm:text-lg lg:text-2xl font-bold text-blue-600">
              {formatFileSize(totalSize)}
            </p>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-green-50">
            <p className="text-xs text-green-600">Uploaded{statusCountsArePageScoped && <span className="text-gray-400"> (this page)</span>}</p>
            <p className="text-base sm:text-lg lg:text-2xl font-bold text-green-600">
              {recordings.filter(r => r.uploadStatus === 'uploaded').length}
            </p>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-yellow-50">
            <p className="text-xs text-yellow-600">Pending{statusCountsArePageScoped && <span className="text-gray-400"> (this page)</span>}</p>
            <p className="text-base sm:text-lg lg:text-2xl font-bold text-yellow-600">
              {recordings.filter(r => r.uploadStatus === 'pending').length}
            </p>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-red-50">
            <p className="text-xs text-red-600">Failed / Other{statusCountsArePageScoped && <span className="text-gray-400"> (this page)</span>}</p>
            <p className="text-base sm:text-lg lg:text-2xl font-bold text-red-600">
              {recordings.filter(r => r.uploadStatus !== 'uploaded' && r.uploadStatus !== 'pending').length}
            </p>
          </Card>
        </div>

        {/* Filters */}
        <Card padding="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <div className="flex-1">
              <SearchInput
                value={search}
                onChange={(val) => { setSearch(val); setPage(1); }}
                placeholder="Search by phone or lead name..."
              />
            </div>
            <div className="flex gap-2">
              <Select
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'uploaded', label: 'Uploaded' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'failed', label: 'Failed' },
                ]}
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                className="min-w-[120px]"
              />
              <div className="flex items-center gap-1.5">
                <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">Rows per page</span>
                <Select
                  options={[
                    { value: 25, label: '25' },
                    { value: 50, label: '50' },
                    { value: 100, label: '100' },
                    { value: 200, label: '200' },
                  ]}
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="min-w-[70px]"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Bulk selection bar */}
        {selectedIds.size > 0 && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm font-medium text-indigo-800">
              {selectedIds.size} recording{selectedIds.size > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="flex items-center gap-2"
                icon={<HiOutlineClipboardList className="w-4 h-4" />}
                onClick={() => setShowAnalysisModal(true)}
              >
                Send for Analysis
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
                Clear
              </Button>
            </div>
          </div>
        )}

        {/* Recordings List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : recordings.length === 0 ? (
          <Card>
            <EmptyState
              icon={<HiOutlineMicrophone className="w-8 h-8 text-gray-400" />}
              title="No recordings found"
              description="Call recordings will appear here after calls are made"
            />
          </Card>
        ) : (
          <Card padding="p-0">
            {/* Desktop Table */}
            <div className="hidden md:block">
              <Table>
                <TableHead>
                  <TableRow hoverable={false}>
                    <TableCell header className="w-10">
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-indigo-600 cursor-pointer"
                        checked={allOnPageSelected}
                        onChange={toggleSelectAll}
                        title="Select all on this page"
                      />
                    </TableCell>
                    <TableCell header className="min-w-[120px] cursor-pointer select-none hover:text-primary-700 transition-colors whitespace-nowrap" onClick={() => handleSort('phoneNumber')}>
                      <span className="inline-flex items-center">Phone <SortIcon field="phoneNumber" /></span>
                    </TableCell>
                    <TableCell header className="min-w-[150px]">Lead</TableCell>
                    <TableCell header className="min-w-[100px]">User</TableCell>
                    <TableCell header className="min-w-[100px] cursor-pointer select-none hover:text-primary-700 transition-colors whitespace-nowrap" onClick={() => handleSort('callType')}>
                      <span className="inline-flex items-center">Type <SortIcon field="callType" /></span>
                    </TableCell>
                    <TableCell header className="min-w-[100px] cursor-pointer select-none hover:text-primary-700 transition-colors whitespace-nowrap" onClick={() => handleSort('uploadStatus')}>
                      <span className="inline-flex items-center">Status <SortIcon field="uploadStatus" /></span>
                    </TableCell>
                    <TableCell header className="min-w-[80px] cursor-pointer select-none hover:text-primary-700 transition-colors whitespace-nowrap" onClick={() => handleSort('duration')}>
                      <span className="inline-flex items-center">Duration <SortIcon field="duration" /></span>
                    </TableCell>
                    <TableCell header className="min-w-[80px] cursor-pointer select-none hover:text-primary-700 transition-colors whitespace-nowrap" onClick={() => handleSort('fileSize')}>
                      <span className="inline-flex items-center">Size <SortIcon field="fileSize" /></span>
                    </TableCell>
                    <TableCell header className="min-w-[80px] cursor-pointer select-none hover:text-primary-700 transition-colors whitespace-nowrap" onClick={() => handleSort('createdAt')}>
                      <span className="inline-flex items-center">Date <SortIcon field="createdAt" /></span>
                    </TableCell>
                    <TableCell header className={`min-w-[200px] ${stickyActionHeaderClass}`}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recordings.map((rec) => (
                    <TableRow key={rec._id}>
                      <TableCell>
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-indigo-600 cursor-pointer"
                          checked={selectedIds.has(rec._id)}
                          onChange={() => toggleSelect(rec._id)}
                        />
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-gray-900 whitespace-nowrap">
                          {rec.phoneNumber}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600 truncate max-w-[150px] block">
                          {rec.leadId?.name || 'Unknown'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600 text-sm">
                          {rec.userId?.firstName} {rec.userId?.lastName}
                        </span>
                      </TableCell>
                      <TableCell>
                        {getCallTypeBadge(rec.callType)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(rec.uploadStatus)}
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600 whitespace-nowrap">
                          {formatDuration(rec.duration)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600 whitespace-nowrap">
                          {formatFileSize(rec.fileSize)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-500 text-sm whitespace-nowrap">
                          {rec.createdAt ? format(new Date(rec.createdAt), 'MMM d, HH:mm') : '-'}
                        </span>
                      </TableCell>
                      <TableCell className={stickyActionCellClass}>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5"
                            icon={<HiOutlinePlay className="w-4 h-4 text-primary-600" />}
                            onClick={() => setSelectedRecording(rec)}
                            title="Play Recording"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-1"
                            icon={<HiOutlineSparkles className="w-4 h-4 text-indigo-600" />}
                            onClick={() => handleCallAnalysis(rec)}
                            title="Call Analysis"
                          >
                            Call Analysis
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5"
                            icon={<HiOutlineDownload className="w-4 h-4 text-blue-600" />}
                            onClick={async () => {
                              try {
                                const response = await api.get(`/recordings/file/${rec._id}`, {
                                  responseType: 'blob'
                                });
                                const url = URL.createObjectURL(response.data);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = recordingDownloadName(rec.fileName, response.data?.type, rec._id);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                URL.revokeObjectURL(url);
                                toast.success('Download started');
                              } catch (error) {
                                toast.error('Failed to download recording');
                              }
                            }}
                            title="Download"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5"
                            icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                            onClick={() => {
                              setRecordingToDelete(rec);
                              setShowDeleteModal(true);
                            }}
                            title="Delete"
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {recordings.map((rec) => (
                <div key={rec._id} className="p-3 sm:p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-start gap-2 min-w-0 flex-1">
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-indigo-600 cursor-pointer mt-1"
                        checked={selectedIds.has(rec._id)}
                        onChange={() => toggleSelect(rec._id)}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 truncate">{rec.phoneNumber}</p>
                        <p className="text-xs sm:text-sm text-gray-500 truncate">
                          {rec.leadId?.name || 'Unknown Lead'}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {getCallTypeBadge(rec.callType)}
                      {getStatusBadge(rec.uploadStatus)}
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                    <span>{rec.userId?.firstName} {rec.userId?.lastName}</span>
                    <span>{formatDuration(rec.duration)} • {formatFileSize(rec.fileSize)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
                    <span>{rec.createdAt ? format(new Date(rec.createdAt), 'MMM d, yyyy HH:mm') : '-'}</span>
                  </div>
                  <div className="flex items-center gap-1.5 pt-2 border-t border-gray-100">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2 flex-1"
                      icon={<HiOutlinePlay className="w-4 h-4 text-primary-600" />}
                      onClick={() => setSelectedRecording(rec)}
                    >
                      <span className="text-xs">Play</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2 flex-1"
                      icon={<HiOutlineSparkles className="w-4 h-4 text-indigo-600" />}
                      onClick={() => handleCallAnalysis(rec)}
                    >
                      <span className="text-xs">Call Analysis</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2 flex-1"
                      icon={<HiOutlineDownload className="w-4 h-4 text-blue-600" />}
                      onClick={async () => {
                        try {
                          const response = await api.get(`/recordings/file/${rec._id}`, {
                            responseType: 'blob'
                          });
                          const url = URL.createObjectURL(response.data);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = recordingDownloadName(rec.fileName, response.data?.type, rec._id);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          URL.revokeObjectURL(url);
                          toast.success('Download started');
                        } catch (error) {
                          toast.error('Failed to download recording');
                        }
                      }}
                    >
                      <span className="text-xs">Download</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2"
                      icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                      onClick={() => {
                        setRecordingToDelete(rec);
                        setShowDeleteModal(true);
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 sm:p-4 border-t border-gray-100">
                <span className="text-xs sm:text-sm text-gray-500">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    <span className="hidden sm:inline">Previous</span>
                    <span className="sm:hidden">Prev</span>
                  </Button>

                  {buildPageList(pagination.page, pagination.totalPages).map(
                    (item, idx) =>
                      item === '...' ? (
                        <span
                          key={`ellipsis-${idx}`}
                          className="px-2 text-sm text-gray-400 select-none"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={item}
                          type="button"
                          onClick={() => setPage(item)}
                          className={
                            page === item
                              ? "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-semibold bg-primary-600 text-white transition-colors"
                              : "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                          }
                        >
                          {item}
                        </button>
                      )
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Recording Modal */}
        <Modal
          isOpen={!!selectedRecording}
          onClose={() => setSelectedRecording(null)}
          title="Call Recording"
          size="md"
        >
          {selectedRecording && (
            <div className="space-y-3 sm:space-y-4">
              <div className="bg-gray-50 p-3 sm:p-4 rounded-xl">
                <div className="flex items-start justify-between gap-2 mb-3 sm:mb-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">
                      {selectedRecording.phoneNumber}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500 truncate">
                      {selectedRecording.leadId?.name || 'Unknown Lead'}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5 sm:mt-1">
                      User: {selectedRecording.userId?.firstName} {selectedRecording.userId?.lastName}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {getCallTypeBadge(selectedRecording.callType)}
                    {getStatusBadge(selectedRecording.uploadStatus)}
                  </div>
                </div>
                <div className="text-xs sm:text-sm text-gray-500 mb-3">
                  {selectedRecording.createdAt
                    ? format(new Date(selectedRecording.createdAt), 'MMM d, yyyy h:mm a')
                    : '-'}
                  {' • '}
                  {formatDuration(selectedRecording.duration)}
                  {' • '}
                  {formatFileSize(selectedRecording.fileSize)}
                </div>
                {audioUrl ? (
                  <TalkTimeAudio
                    className="w-full"
                    src={audioUrl}
                    talkDuration={selectedRecording.duration}
                  />
                ) : (
                  <div className="flex items-center justify-center py-4">
                    <Spinner size="sm" />
                    <span className="ml-2 text-gray-500 text-sm">Loading audio...</span>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  icon={<HiOutlineDownload className="w-4 h-4" />}
                  onClick={async () => {
                    try {
                      const response = await api.get(`/recordings/file/${selectedRecording._id}`, {
                        responseType: 'blob'
                      });
                      const url = URL.createObjectURL(response.data);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = recordingDownloadName(selectedRecording.fileName, response.data?.type, selectedRecording._id);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      URL.revokeObjectURL(url);
                      toast.success('Download started');
                    } catch (error) {
                      toast.error('Failed to download recording');
                    }
                  }}
                >
                  Download
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  icon={<HiOutlineTrash className="w-4 h-4" />}
                  onClick={() => {
                    setSelectedRecording(null);
                    setRecordingToDelete(selectedRecording);
                    setShowDeleteModal(true);
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          )}
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setRecordingToDelete(null);
          }}
          title="Delete Recording"
          size="sm"
        >
          <div className="space-y-3 sm:space-y-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this recording? This action cannot be undone.
            </p>
            {recordingToDelete && (
              <div className="bg-red-50 p-3 rounded-lg">
                <p className="text-sm font-medium text-gray-900">
                  {recordingToDelete.phoneNumber}
                </p>
                <p className="text-xs text-gray-500">
                  {recordingToDelete.leadId?.name || 'Unknown Lead'} • {formatDuration(recordingToDelete.duration)}
                </p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowDeleteModal(false);
                  setRecordingToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleDeleteRecording}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>

        {/* Send for Analysis Modal */}
        <Modal
          isOpen={showAnalysisModal}
          onClose={() => setShowAnalysisModal(false)}
          title="Send for Call Analysis"
          size="sm"
        >
          <div className="space-y-3 sm:space-y-4">
            <p className="text-sm text-gray-600">
              {selectedIds.size} recording{selectedIds.size > 1 ? 's' : ''} will be queued for
              transcription (with speaker detection) and AI analysis. They process one at a time
              in the background — track progress under{' '}
              <span className="font-medium text-indigo-600">Communication → Call Analysis</span>.
            </p>
            {!deepgramEnabled && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                Transcription is not configured on the server. Ask your administrator to add DEEPGRAM_API_KEY.
              </div>
            )}
            {/* Ollama is the default/only active analysis AI — warn when it is
                not running and no cloud AI key is configured as backup. */}
            {deepgramEnabled && (providersData?.llm?.ollama?.running === false) && (providersData?.llm?.available?.openai === false) && (providersData?.llm?.available?.anthropic === false) && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                <p className="font-medium mb-1">Ollama is not running on the server</p>
                <p>
                  Call analysis uses local Ollama. Ask your administrator to start it
                  (<code className="px-1 py-0.5 bg-red-100 rounded text-xs">ollama serve</code>) and
                  install a model (<code className="px-1 py-0.5 bg-red-100 rounded text-xs">ollama pull llama3.1</code>),
                  or configure a cloud AI key (OPENAI_API_KEY / ANTHROPIC_API_KEY) in the backend .env.
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                <Select
                  value={analysisLanguage}
                  onChange={(e) => setAnalysisLanguage(e.target.value)}
                  options={LANGUAGE_OPTIONS}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">AI Provider</label>
                <Select
                  value={analysisProvider || defaultProvider}
                  onChange={(e) => setAnalysisProvider(e.target.value)}
                  options={buildProviderOptions(providersData?.llm?.available || {}, providersData?.llm?.ollama || {})}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowAnalysisModal(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSendForAnalysis}
                disabled={sendingAnalysis || !deepgramEnabled}
                className="flex items-center gap-2"
              >
                {sendingAnalysis ? <Spinner size="sm" /> : <HiOutlineSparkles className="w-4 h-4" />}
                Send for Analysis
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </Layout>
  );
};

export default RecordingsList;